/**
 * Attorney Digest Preferences Component
 * 
 * Allows attorneys to manage their weekly digest email preferences:
 * - Enable/disable weekly digest (opt-in by default - OFF)
 * - Choose which sections to include (deadlines, predictions, actions)
 * - Preview digest content
 */

import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  Bell, 
  BellOff, 
  Calendar,
  Target,
  TrendingUp,
  Loader2,
  Check,
  X,
  Eye,
  Send,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface DigestPreferences {
  attorney_id: string;
  weekly_digest_enabled: boolean;
  sections: {
    deadlines: boolean;
    predictions: boolean;
    actions: boolean;
  };
  delivery_day: string;
  delivery_hour: number;
}

interface DigestSettingsProps {
  attorneyId: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function DigestSettings({ attorneyId, isOpen, onClose }: DigestSettingsProps) {
  const [preferences, setPreferences] = useState<DigestPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && attorneyId) {
      fetchPreferences();
    }
  }, [isOpen, attorneyId]);

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('attorney_token');
      const response = await fetch(
        `${BACKEND_URL}/api/v1/bi-timeline/digest/preferences/${attorneyId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setPreferences(data.preferences);
      } else {
        throw new Error('Failed to load preferences');
      }
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
      toast.error('Failed to load digest preferences');
    } finally {
      setLoading(false);
    }
  };

  const updatePreferences = async (updates: Partial<DigestPreferences>) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('attorney_token');
      const response = await fetch(
        `${BACKEND_URL}/api/v1/bi-timeline/digest/preferences/${attorneyId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updates)
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPreferences(data.preferences);
        toast.success('Preferences updated');
      } else {
        throw new Error('Failed to update preferences');
      }
    } catch (error) {
      console.error('Failed to update preferences:', error);
      toast.error('Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDigest = () => {
    if (preferences) {
      updatePreferences({ weekly_digest_enabled: !preferences.weekly_digest_enabled });
    }
  };

  const handleToggleSection = (section: 'deadlines' | 'predictions' | 'actions') => {
    if (preferences) {
      updatePreferences({
        sections: {
          ...preferences.sections,
          [section]: !preferences.sections[section]
        }
      });
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setPreviewHtml(null);
    try {
      const token = localStorage.getItem('attorney_token');
      const response = await fetch(
        `${BACKEND_URL}/api/v1/bi-timeline/digest/preview/${attorneyId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPreviewHtml(data.preview.html);
      } else {
        throw new Error('Failed to generate preview');
      }
    } catch (error) {
      console.error('Failed to preview digest:', error);
      toast.error('Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSendNow = async () => {
    try {
      const token = localStorage.getItem('attorney_token');
      const response = await fetch(
        `${BACKEND_URL}/api/v1/bi-timeline/digest/send/${attorneyId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'sent') {
          toast.success('Digest email sent!');
        } else if (data.status === 'skipped') {
          toast.info('Digest not enabled - enable it first');
        } else {
          toast.info(data.message || 'Digest processed');
        }
      } else {
        throw new Error('Failed to send digest');
      }
    } catch (error) {
      console.error('Failed to send digest:', error);
      toast.error('Failed to send digest');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Weekly Digest Settings</h2>
              <p className="text-sm text-blue-100">Manage your Monday morning email digest</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            data-testid="close-digest-settings"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : preferences ? (
            <div className="space-y-6">
              {/* Master Toggle */}
              <div 
                className={`p-4 rounded-xl border-2 transition-all ${
                  preferences.weekly_digest_enabled 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {preferences.weekly_digest_enabled ? (
                      <Bell className="w-6 h-6 text-green-600" />
                    ) : (
                      <BellOff className="w-6 h-6 text-gray-400" />
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">Weekly Digest Email</h3>
                      <p className="text-sm text-gray-500">
                        Receive a summary of all your cases every Monday at 8 AM
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleToggleDigest}
                    disabled={saving}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      preferences.weekly_digest_enabled ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                    data-testid="toggle-weekly-digest"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        preferences.weekly_digest_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Section Toggles */}
              <div className={`space-y-3 ${!preferences.weekly_digest_enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Include in Digest
                </h3>
                
                {/* Deadlines Section */}
                <div 
                  className={`p-4 rounded-lg border transition-all cursor-pointer ${
                    preferences.sections.deadlines 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleToggleSection('deadlines')}
                  data-testid="toggle-deadlines-section"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${preferences.sections.deadlines ? 'bg-blue-500' : 'bg-gray-200'}`}>
                      <Calendar className={`w-4 h-4 ${preferences.sections.deadlines ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">Deadline Status</h4>
                      <p className="text-sm text-gray-500">Upcoming, overdue, and completed deadlines</p>
                    </div>
                    {preferences.sections.deadlines && (
                      <Check className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                </div>

                {/* Predictions Section */}
                <div 
                  className={`p-4 rounded-lg border transition-all cursor-pointer ${
                    preferences.sections.predictions 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleToggleSection('predictions')}
                  data-testid="toggle-predictions-section"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${preferences.sections.predictions ? 'bg-purple-500' : 'bg-gray-200'}`}>
                      <TrendingUp className={`w-4 h-4 ${preferences.sections.predictions ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">Predictions & Insights</h4>
                      <p className="text-sm text-gray-500">Settlement probability and risk alerts</p>
                    </div>
                    {preferences.sections.predictions && (
                      <Check className="w-5 h-5 text-purple-600" />
                    )}
                  </div>
                </div>

                {/* Actions Section */}
                <div 
                  className={`p-4 rounded-lg border transition-all cursor-pointer ${
                    preferences.sections.actions 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleToggleSection('actions')}
                  data-testid="toggle-actions-section"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${preferences.sections.actions ? 'bg-orange-500' : 'bg-gray-200'}`}>
                      <Target className={`w-4 h-4 ${preferences.sections.actions ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">Recommended Actions</h4>
                      <p className="text-sm text-gray-500">Priority tasks for the week</p>
                    </div>
                    {preferences.sections.actions && (
                      <Check className="w-5 h-5 text-orange-600" />
                    )}
                  </div>
                </div>
              </div>

              {/* Preview */}
              {previewHtml && (
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Preview</h3>
                  <div 
                    className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-80 overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Failed to load preferences
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handlePreview}
            disabled={previewing || loading}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            data-testid="preview-digest-btn"
          >
            {previewing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            Preview Digest
          </button>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleSendNow}
              disabled={!preferences?.weekly_digest_enabled}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="send-digest-now-btn"
            >
              <Send className="w-4 h-4" />
              Send Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
